import { Controller } from "@hotwired/stimulus"

const CURRENT_CAPTION_CLASSES = ["border-warning/60", "bg-warning/15", "text-base-content"]
const LOCKED_CAPTION_CLASSES = ["border-base-content/30", "bg-base-100/25", "text-base-content/60"]
const CAPTION_STATE_CLASSES = [...CURRENT_CAPTION_CLASSES, ...LOCKED_CAPTION_CLASSES]
const SELECTED_COMPLETED_CLASSES = ["ring-2", "ring-warning", "ring-offset-1"]

export default class extends Controller {
  static targets = [
    "applyControl", "preview", "stateIcon", "lockIcon", "caption", "correctionControl", "completedIndicator"
  ]

  static values = {
    state: String,
    applied: Boolean
  }

  connect() {
    this.reconcile()
  }

  stateValueChanged() {
    this.reconcile()
  }

  appliedValueChanged() {
    this.reconcile()
  }

  // Reveal the mark immediately while the form submits, but wait for Turbo before treating it as persisted.
  markSubmitted(event) {
    if (this.element.getAttribute("aria-busy") === "true") {
      event.preventDefault()
      return
    }

    if (!this.hasPreviewTarget || this.previewTarget.children.length === 0) return

    this.previewTarget.classList.remove("invisible")
    if (this.hasStateIconTarget) this.stateIconTarget.classList.add("hidden")
    if (this.hasCaptionTarget) this.captionTarget.classList.add("hidden")
    this.applyControlTarget.classList.add("pointer-events-none")
    this.applyControlTarget.setAttribute("aria-disabled", "true")
    this.element.setAttribute("aria-busy", "true")
  }

  // A failed submission has no Turbo replacement, so restore the placement for another attempt.
  rollback() {
    if (this.appliedValue) return

    if (this.hasPreviewTarget) this.previewTarget.classList.add("invisible")
    this.element.removeAttribute("aria-busy")
    if (this.hasApplyControlTarget) {
      this.applyControlTarget.classList.remove("pointer-events-none")
      this.applyControlTarget.removeAttribute("aria-disabled")
    }
    if (this.hasStateIconTarget) this.stateIconTarget.classList.remove("hidden")
    if (this.hasCaptionTarget) this.captionTarget.classList.remove("hidden")
    this.reconcile()
  }

  focus() {
    this.reconcile()
    if (this.hasApplyControlTarget && !this.applyControlTarget.disabled) {
      this.applyControlTarget.focus({preventScroll: true})
    } else if (this.hasCorrectionControlTarget && !this.correctionControlTarget.disabled) {
      this.correctionControlTarget.focus({preventScroll: true})
    }
  }

  reconcile() {
    const current = this.stateValue === "current"
    this.element.toggleAttribute("aria-current", current)
    if (current) this.element.setAttribute("aria-current", "step")

    if (this.appliedValue) {
      this.reconcileCompleted(current)
    } else {
      this.reconcileIncomplete(current)
    }
  }

  reconcileCompleted(selected) {
    SELECTED_COMPLETED_CLASSES.forEach((className) => this.element.classList.toggle(className, selected))

    if (this.hasCompletedIndicatorTarget) this.completedIndicatorTarget.classList.toggle("hidden", selected)
    if (this.hasCorrectionControlTarget) {
      this.correctionControlTarget.classList.toggle("hidden", !selected)
      this.correctionControlTarget.disabled = !selected
    }

    const prefix = selected ? "Selected completed" : "Completed"
    this.element.setAttribute(
      "aria-label",
      `${prefix} ${this.element.dataset.placementKind} placement ${this.element.dataset.signingPlacementNumber} on page ${this.element.dataset.placementPageNumber}`
    )
  }

  reconcileIncomplete(current) {
    this.element.classList.remove(...SELECTED_COMPLETED_CLASSES)
    if (!this.hasApplyControlTarget) return

    this.applyControlTarget.disabled = !current
    if (this.element.getAttribute("aria-busy") !== "true") {
      this.applyControlTarget.classList.remove("pointer-events-none")
      this.applyControlTarget.removeAttribute("aria-disabled")
    }
    if (this.hasStateIconTarget) this.stateIconTarget.classList.toggle("hidden", !current)
    if (this.hasLockIconTarget) this.lockIconTarget.classList.toggle("hidden", current)
    if (this.hasCaptionTarget) {
      this.captionTarget.classList.remove(...CAPTION_STATE_CLASSES)
      this.captionTarget.classList.add(...(current ? CURRENT_CAPTION_CLASSES : LOCKED_CAPTION_CLASSES))
    }

    const label = current ?
      `Apply ${this.element.dataset.placementKind} placement ${this.element.dataset.signingPlacementNumber} on page ${this.element.dataset.placementPageNumber}` :
      `${this.element.dataset.placementActionLabel} placement ${this.element.dataset.signingPlacementNumber} is locked until earlier placements are completed`
    this.applyControlTarget.setAttribute("aria-label", label)
    this.element.setAttribute("aria-label", label)
  }
}
